import { ChangeEvent, useState } from "react";
import Input from "../../ui/input";
import { useDispatch } from "react-redux";
import { setBasicInformation } from "../../../features/gms-proposal-forms/basic-information/basic-information.slice";
import { formatTinNumber } from "../../../utils/tin-number-formater.utils";
import { customStyle } from "../../../constant/custom-style";

interface Props {
    modalTitle: string;
    modalSubTitle: string;
    onConfirm: () => void;
    onCancel: () => void;
    onDeny?: (reason: string) => void;
    isDeny?: boolean;
    isLoading?: boolean;
    notes?: string; 
    onNotesChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    notesTitle?: string;
    notesPlaceholder?: string;
    notesInstruction?: string;

    isMissingTinAndSec?: boolean;
    tinNumber?: string;
    secNumber?: string;
}

export default function ConfirmDefaultModal({
    modalTitle,
    modalSubTitle,
    onCancel,
    onConfirm,
    onDeny,
    isDeny = false,
    isLoading = false,
    notes = "",
    onNotesChange,
    notesTitle = "Reason for denial",
    notesPlaceholder = "Enter the reason for denying this submission...",
    notesInstruction = "Please provide a clear reason for the requester.",

    isMissingTinAndSec = false,
    tinNumber: initialTinNumber = "",
    secNumber: initialSecNumber = "",
}: Props) {

    const dispatch = useDispatch();

    const handleDeny = () => {
        const reason = notes.trim();
        if (!reason || isLoading) return;
        onDeny?.(reason);
    };

    // const [tinNumber, setTinNumber] = useState(initialTinNumber);
    // const [secNumber, setSecNumber] = useState(initialSecNumber);

    // const hasTinOrSec = Boolean(tinNumber.trim() || secNumber.trim());

    // const handleConfirm = () => {
    //     if (isLoading) return;

    //     if (isMissingTinAndSec && !hasTinOrSec) return;

    //     onConfirm({
    //         tinNumber: tinNumber.trim(),
    //         secNumber: secNumber.trim(),
    //     });
    // };

    const handleChange = ( e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> ) => {
        const { name, value } = e.target;
        if (name === 'tinNumber' || name === 'secNumber') {
            dispatch(setBasicInformation({ field: name , value }));
        } 
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]">
            <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                <div className="flex flex-col p-5 sm:p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-full ${isDeny ? "bg-red-50 text-red-500" : "bg-[#186cb2]/10 text-[#186cb2]"}`}>
                            {isDeny ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.84 2.73 17a2 2 0 0 0 1.73 3h15.08a2 2 0 0 0 1.73-3L13.7 3.84a2 2 0 0 0-3.4 0z" />
                                </svg>
                            )}
                        </div>
                        <div className="mt-3">
                            <h2 className="text-sm font-semibold text-gray-900">
                                {modalTitle}
                            </h2>
                            <p className="mx-auto mt-1.5 text-xs leading-relaxed text-gray-500">
                                {modalSubTitle}
                            </p>
                        </div>
                    </div>
                    {isDeny && (
                        <div className="mt-4">
                            <label className="mb-1.5 block text-left text-xs font-semibold text-gray-700">
                                {notesTitle}
                                <span className="ml-1 text-red-500">*</span>
                            </label>
                            <textarea
                                value={notes}
                                onChange={onNotesChange}
                                disabled={isLoading}
                                rows={4}
                                placeholder={notesPlaceholder}
                                className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-red-300 focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:bg-gray-50"
                            />
                            <p className="mt-1 text-left text-[10px] text-gray-400">
                                {notesInstruction}
                            </p>
                        </div>
                    )}
                    {isMissingTinAndSec && (
                        <div className="mt-5 space-y-3">
                            <div className="rounded-lg border border-[#186cb2]/15 bg-[#eef6fc] p-3">
                                <div className="flex items-start gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="mt-0.5 h-4 w-4 shrink-0 text-[#186cb2]">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 11v5m0-8h.01" />
                                    </svg>
                                    <p className="text-[11px] leading-relaxed text-[#155d9b]">
                                        No TIN or SEC number was detected for this proposal. Please provide at least one of the following before proceeding.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                                    TIN Number
                                </label>
                                {/* <input
                                    type="text"
                                    value={tinNumber}
                                    onChange={(e) => setTinNumber(e.target.value)}
                                    disabled={isLoading}
                                    placeholder="Enter TIN number"
                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-50"
                                /> */}
                                <div className={`${initialTinNumber ? '' : '' } flex rounded border overflow-hidden focus-within:border-[#186cb2] border-gray-300`}>
                                    <Input
                                        type="text"
                                        name={'tinNumber'}
                                        value={formatTinNumber(initialTinNumber)}
                                        isCustomStyle={true}
                                        customCss={`rounded-tr rounded-br w-full text-xs px-2.5 py-2.5 outline-none ${!initialTinNumber ? '' : customStyle }`}
                                        onChange={handleChange}
                                        placeholder="000-000-000-00000"
                                        required={true}
                                        // disabled={!isComplete}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                                    SEC Number
                                </label>
                                {/* <input
                                    type="text"
                                    value={secNumber}
                                    onChange={(e) => setSecNumber(e.target.value)}
                                    disabled={isLoading}
                                    placeholder="Enter SEC number"
                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-50"
                                /> */}
                                <div className={`${initialSecNumber ? '' : '' } flex rounded border overflow-hidden focus-within:border-[#186cb2] border-gray-300 `}>
                                    <Input
                                        type="text"
                                        name={'secNumber'}
                                        value={initialSecNumber}
                                        isCustomStyle={true}
                                        customCss={`rounded-tr rounded-br w-full text-xs px-2.5 py-2.5 outline-none ${!initialSecNumber ? '' : customStyle }`}
                                        onChange={handleChange}
                                        placeholder="Enter SEC Number"
                                        maxLength={20}
                                        // disabled={!isComplete}
                                    />
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400">
                                * At least one field is required. You may provide both.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/70 p-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onCancel}
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[110px]"
                    >
                        Back
                    </button>

                    {isDeny ? (
                        <button
                            type="button"
                            onClick={handleDeny}
                            disabled={isLoading || !notes.trim()}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-red-500/20 transition hover:bg-red-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[110px]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Denying...
                                </>
                            ) : (
                                "Yes, Deny"
                            )}
                        </button>
                    ) : isMissingTinAndSec ? (
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[110px]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}
                        </button>
                    ): (
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[110px]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Confirming...
                                </>
                            ) : (
                                "Yes, Confirm"
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}